// ---------------------------------------------------------
// Datei: store/documentState.ts
// Zweck: Zentraler Speicher für den Zustand der Dokumente über die Navigation hinweg
// Besonderheiten:
// - Persistenter Zustand für Dokumente zwischen Seitenaufrufen
// - Speichert Upload-Status und Fortschritt
// - Ermöglicht optimistic Updates
// ---------------------------------------------------------

import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { getDocumentTypes } from "../services/api/documentTypes";
import { deleteProjectDocument as apiDeleteDocument } from "../services/api/ops";
import { DocumentType, UploadedDocumentFile } from "../types/documents";
import {
  mergeUploadedDocumentFiles,
  normalizeUploadedDocumentFiles,
  stripUploadedDocumentFilesRuntimeData,
} from "../utils/uploadedDocumentFiles";
import {
  resolveSubstitutes,
  calculateProgressWithSubstitutes,
} from "../services/substituteResolver";

// Speichert den Zustand der Dokumente nach Phase
interface DocumentState {
  // Dokumenten-Zustand pro Phase
  documents: Record<string, DocumentType[]>;
  // Progress pro Phase
  progress: Record<string, { completed: number; required: number }>;
  // Phase-Status (für Häkchen-Persistenz)
  phases: Record<
    string,
    { status: "pending" | "completed"; completedAt?: string; completedByEmail?: string }
  >;
  // Loading-Status pro Phase
  loading: Record<string, boolean>;
  // Error pro Phase
  error: Record<string, string | null>;
  // Dokument-Daten für Vorschau (Base64-Strings für Bilder/PDFs)
  documentData: Record<string, string>; // Key: `${phaseId}-${docId}`
  // Mehrere Upload-Referenzen pro Dokument
  documentFiles: Record<string, UploadedDocumentFile[]>; // Key: `${phaseId}-${docId}`

  // Aktionen
  fetchDocuments: (phaseId: string) => Promise<void>;
  setHubSpotDocumentStatus: (
    phaseId: string,
    documents: any[],
    progress: { required: number; completed: number }
  ) => void;
  updateDocumentStatus: (
    phaseId: string,
    docId: string,
    status: "completed" | "pending" | "substituted"
  ) => void;
  updatePhaseStatus: (
    phaseId: string,
    status: "pending" | "completed",
    completedAt?: string,
    completedByEmail?: string
  ) => void;
  getPhaseDocuments: (phaseId: string) => DocumentType[];
  getPhaseProgress: (phaseId: string) => {
    completed: number;
    required: number;
  };
  getPhaseStatus: (
    phaseId: string
  ) => { status: "pending" | "completed"; completedAt?: string; completedByEmail?: string } | null;
  isPhaseLoading: (phaseId: string) => boolean;
  getPhaseError: (phaseId: string) => string | null;
  // Dokument-Daten speichern/laden
  saveDocumentData: (phaseId: string, docId: string, data: string) => void;
  getDocumentData: (phaseId: string, docId: string) => string | null;
  // FileId für Dokument speichern (nach HubSpot-Upload)
  saveDocumentFileId: (phaseId: string, docId: string, fileId: string) => void;
  getDocumentFileId: (phaseId: string, docId: string) => string | null;
  saveDocumentFiles: (phaseId: string, docId: string, files: UploadedDocumentFile[]) => void;
  appendDocumentFiles: (phaseId: string, docId: string, files: UploadedDocumentFile[]) => UploadedDocumentFile[];
  getDocumentFiles: (phaseId: string, docId: string) => UploadedDocumentFile[];
  // Dokument löschen (Backend + Store)
  deleteDocument: (phaseId: string, docId: string) => Promise<void>;
}

const getDocumentKey = (phaseId: string, docId: string) => `${phaseId}-${docId}`;

const getPrimaryFileId = (files: UploadedDocumentFile[]) =>
  [...files].reverse().find((file) => file.fileId)?.fileId;

const getDocumentValueIds = (files: UploadedDocumentFile[]) =>
  files.map((file) => file.fileId || file.url || file.id).filter(Boolean) as string[];

const recalculateProgress = (docs: DocumentType[]) => {
  const resolvedDocs = resolveSubstitutes(docs);
  return calculateProgressWithSubstitutes(resolvedDocs);
};

const withDocumentFiles = (
  docs: DocumentType[],
  docId: string,
  files: UploadedDocumentFile[],
  status: "completed" | "pending" = files.length > 0 ? "completed" : "pending"
) =>
  docs.map((doc) => {
    if (doc._id !== docId) return doc;
    return {
      ...doc,
      status,
      fileId: getPrimaryFileId(files) || (status === "completed" ? doc.fileId : undefined),
      fileIds: status === "completed" ? getDocumentValueIds(files) : [],
      uploadedFiles: status === "completed" ? files : [],
    };
  });

const isRuntimeDataUrl = (value?: string | null): boolean =>
  typeof value === "string" && value.startsWith("data:");

export const stripDocumentRuntimeData = (document: DocumentType): DocumentType => {
  const nextDocument: DocumentType = { ...document };

  if (nextDocument.uploadedFiles) {
    nextDocument.uploadedFiles = stripUploadedDocumentFilesRuntimeData(nextDocument.uploadedFiles);
  }

  if (isRuntimeDataUrl(nextDocument.fileUrl)) {
    delete nextDocument.fileUrl;
  }

  return nextDocument;
};

export const stripDocumentsRuntimeData = (
  documents: Record<string, DocumentType[]>
): Record<string, DocumentType[]> =>
  Object.fromEntries(
    Object.entries(documents).map(([phaseId, phaseDocuments]) => [
      phaseId,
      phaseDocuments.map(stripDocumentRuntimeData),
    ])
  );

const stripDocumentDataRuntimeData = (
  documentData: Record<string, string>
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(documentData).filter(([, value]) => !isRuntimeDataUrl(value))
  );

function isQuotaExceededError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const name = "name" in error && typeof error.name === "string" ? error.name : undefined;
  const code = "code" in error && typeof error.code === "number" ? error.code : undefined;
  const message =
    "message" in error && typeof error.message === "string" ? error.message.toLowerCase() : "";
  return (
    name === "QuotaExceededError" ||
    name === "NS_ERROR_DOM_QUOTA_REACHED" ||
    code === 22 ||
    code === 1014 ||
    message.includes("quota") ||
    message.includes("exceeded")
  );
}

function createSafeDocumentStorage() {
  const memoryStorage = new Map<string, string>();
  const fallbackStorage = {
    getItem: (key: string) => memoryStorage.get(key) ?? null,
    setItem: (key: string, value: string) => {
      memoryStorage.set(key, value);
    },
    removeItem: (key: string) => {
      memoryStorage.delete(key);
    },
  };

  if (
    typeof window === "undefined" ||
    !window.localStorage ||
    typeof window.localStorage.getItem !== "function" ||
    typeof window.localStorage.setItem !== "function" ||
    typeof window.localStorage.removeItem !== "function"
  ) {
    return fallbackStorage;
  }

  return {
    getItem: (key: string) => {
      try {
        return window.localStorage.getItem(key) ?? memoryStorage.get(key) ?? null;
      } catch {
        return memoryStorage.get(key) ?? null;
      }
    },
    setItem: (key: string, value: string) => {
      try {
        window.localStorage.setItem(key, value);
        memoryStorage.delete(key);
        return;
      } catch (error) {
        if (isQuotaExceededError(error)) {
          try {
            window.localStorage.removeItem(key);
            window.localStorage.setItem(key, value);
            memoryStorage.delete(key);
            console.warn("[documentState] localStorage quota recovered by replacing persisted document state.");
            return;
          } catch (retryError) {
            console.warn("[documentState] localStorage quota exceeded, using memory fallback.", retryError);
          }
        } else {
          console.warn("[documentState] localStorage write failed, using memory fallback.", error);
        }

        memoryStorage.set(key, value);
      }
    },
    removeItem: (key: string) => {
      try {
        window.localStorage.removeItem(key);
      } catch {
        // Speicher-Fallback unten konsistent halten.
      }
      memoryStorage.delete(key);
    },
  };
}

// Store mit Persistenz
export const useDocumentStore = create<DocumentState>()(
  persist(
    (set, get) => ({
      documents: {},
      progress: {},
      phases: {},
      loading: {},
      error: {},
      documentData: {},
      documentFiles: {},

      // Dokument löschen
      deleteDocument: async (phaseId: string, docId: string) => {
        try {
          // 1. Backend-Call (HubSpot Property leeren)
          await apiDeleteDocument(phaseId, docId);

          // 2. Store aktualisieren (Status auf pending, fileIds leeren)
          const currentDocs = get().documents[phaseId] || [];
          const updatedDocs = withDocumentFiles(currentDocs, docId, [], "pending");

          // Progress neu berechnen
          const { required: requiredCount, completed: completedCount } =
            recalculateProgress(updatedDocs);

          const dataKey = getDocumentKey(phaseId, docId);
          set((state) => {
            const { [dataKey]: _removed, ...remainingData } = state.documentData;
            const { [dataKey]: _removedFiles, ...remainingFiles } = state.documentFiles;
            return {
              documents: { ...state.documents, [phaseId]: updatedDocs },
              progress: {
                ...state.progress,
                [phaseId]: { required: requiredCount, completed: completedCount },
              },
              documentData: remainingData,
              documentFiles: remainingFiles,
            };
          });

          console.log("[DOCUMENT-DELETED]", { phaseId, docId });
        } catch (error) {
          console.error("[DOCUMENT-DELETE-ERROR]", error);
          // Optional: Error-State setzen
        }
      },

      // Dokumente für eine Phase laden
      fetchDocuments: async (phaseId: string) => {
        if (!phaseId) return;

        // Loading-Status setzen
        set((state) => ({
          loading: { ...state.loading, [phaseId]: true },
        }));

        try {
          // Wenn Dokumente bereits geladen sind, nicht erneut laden
          const existingDocs = get().documents[phaseId];
          if (existingDocs && existingDocs.length > 0) {
            set((state) => ({
              loading: { ...state.loading, [phaseId]: false },
            }));
            return;
          }

          // Dokumente vom API laden
          const documentTypes = await getDocumentTypes(phaseId);

          if (Array.isArray(documentTypes)) {
            // Dokumente mit Status-Info ergänzen (für MVP)
            // In Produktion würden hier echte Status-Daten vom Backend kommen
            const docsWithStatus = documentTypes.map((doc) => ({
              ...doc,
              status: "pending" as const,
            }));

            // Ersatzdokumente auflösen und Status aktualisieren
            const resolvedDocs = resolveSubstitutes(docsWithStatus);

            // Progress mit Ersatzdokumenten berechnen
            const { required: requiredCount, completed: completedCount } =
              calculateProgressWithSubstitutes(resolvedDocs);

            // ✅ B-01: Strategic Logging (B-22 Learning)
            console.log("[STORE-SYNC]", {
              phaseId,
              docsCount: docsWithStatus.length,
              required: requiredCount,
              completed: completedCount,
            });

            // State aktualisieren
            set((state) => ({
              documents: { ...state.documents, [phaseId]: docsWithStatus },
              progress: {
                ...state.progress,
                [phaseId]: {
                  required: requiredCount,
                  completed: completedCount,
                },
              },
              loading: { ...state.loading, [phaseId]: false },
              error: { ...state.error, [phaseId]: null },
            }));
          }
        } catch (err: any) {
          const errorMessage =
            err.message || "Fehler beim Laden der Dokumenttypen";

          set((state) => ({
            loading: { ...state.loading, [phaseId]: false },
            error: { ...state.error, [phaseId]: errorMessage },
          }));
        }
      },

      // HubSpot-Dokument-Status setzen (für HubSpot-Deals)
      setHubSpotDocumentStatus: (
        phaseId: string,
        documents: any[],
        progress: { required: number; completed: number }
      ) => {
        // State aktualisieren mit vorbereiteten Dokumenten
        // Dokumente die bereits lokal via Drive hochgeladen wurden behalten ihren Status
        set((state) => {
          const nextDocumentFiles = { ...state.documentFiles };
          const mergedDocs = documents.map((doc) => {
            const key = getDocumentKey(phaseId, doc._id);
            const driveUrl = state.documentData?.[key];
            const mergedFiles = mergeUploadedDocumentFiles(
              state.documentFiles[key],
              normalizeUploadedDocumentFiles((doc as any).uploadedFiles),
              normalizeUploadedDocumentFiles((doc as any).fileIds),
              normalizeUploadedDocumentFiles((doc as any).fileId),
              normalizeUploadedDocumentFiles(driveUrl)
            );

            if (mergedFiles.length > 0) {
              nextDocumentFiles[key] = mergedFiles;
              return {
                ...doc,
                fileId: getPrimaryFileId(mergedFiles) || (doc as any).fileId,
                fileIds: getDocumentValueIds(mergedFiles),
                uploadedFiles: mergedFiles,
                status: 'completed',
              };
            }
            return doc;
          });
          const completedCount = mergedDocs.filter((d) => d.status === 'completed' && d.required).length;
          const mergedProgress = { ...progress, completed: Math.max(progress.completed, completedCount) };
          return {
            documents: { ...state.documents, [phaseId]: mergedDocs },
            progress: { ...state.progress, [phaseId]: mergedProgress },
            loading: { ...state.loading, [phaseId]: false },
            documentFiles: nextDocumentFiles,
          };
        });

        console.log("[HUBSPOT-DOCS-LOADED]", {
          phaseId,
          docsCount: documents.length,
          progress,
        });
      },

      // Status eines Dokuments aktualisieren (optimistic update)
      updateDocumentStatus: (
        phaseId: string,
        docId: string,
        status: "completed" | "pending" | "substituted"
      ) => {
        const currentDocs = get().documents[phaseId] || [];

        // Dokument finden und Status aktualisieren
        const updatedDocs = currentDocs.map((doc) =>
          doc._id === docId ? { ...doc, status } : doc
        );

        // Ersatzdokumente auflösen
        const resolvedDocs = resolveSubstitutes(updatedDocs);

        // Progress mit Ersatzdokumenten berechnen
        const { required: requiredCount, completed: completedCount } =
          calculateProgressWithSubstitutes(resolvedDocs);

        // ✅ B-01: Strategic Logging (B-22 Learning)
        console.log("[PROGRESS-UPDATE]", {
          phaseId,
          docId,
          status,
          required: requiredCount,
          completed: completedCount,
        });

        // State aktualisieren
        set((state) => ({
          documents: { ...state.documents, [phaseId]: updatedDocs },
          progress: {
            ...state.progress,
            [phaseId]: { required: requiredCount, completed: completedCount },
          },
        }));
      },

      // Dokumente für eine Phase abrufen
      getPhaseDocuments: (phaseId: string) => {
        return get().documents[phaseId] || [];
      },

      // Progress für eine Phase abrufen
      getPhaseProgress: (phaseId: string) => {
        return get().progress[phaseId] || { completed: 0, required: 0 };
      },

      // Phase-Status aktualisieren (für B-02 Completion)
      updatePhaseStatus: (
        phaseId: string,
        status: "pending" | "completed",
        completedAt?: string,
        completedByEmail?: string
      ) => {
        console.log("[PHASE-STATUS-UPDATE]", { phaseId, status, completedAt, completedByEmail });

        set((state) => ({
          phases: {
            ...state.phases,
            [phaseId]: { status, completedAt, completedByEmail },
          },
        }));
      },

      // Phase-Status abrufen
      getPhaseStatus: (phaseId: string) => {
        return get().phases[phaseId] || null;
      },

      // Prüfen, ob Dokumente für eine Phase geladen werden
      isPhaseLoading: (phaseId: string) => {
        return get().loading[phaseId] || false;
      },

      // Fehler für eine Phase abrufen
      getPhaseError: (phaseId: string) => {
        return get().error[phaseId] || null;
      },

      // Dokument-Daten speichern (für Vorschau)
      saveDocumentData: (phaseId: string, docId: string, data: string) => {
        const key = `${phaseId}-${docId}`;
        set((state) => ({
          documentData: { ...state.documentData, [key]: data },
        }));
        console.log("[DOCUMENT-DATA-SAVED]", {
          phaseId,
          docId,
          dataLength: data.length,
        });
      },

      // Dokument-Daten abrufen (für Vorschau)
      getDocumentData: (phaseId: string, docId: string) => {
        const key = `${phaseId}-${docId}`;
        return get().documentData[key] || null;
      },

      // FileId für Dokument speichern (nach HubSpot-Upload)
      saveDocumentFileId: (phaseId: string, docId: string, fileId: string) => {
        get().appendDocumentFiles(phaseId, docId, normalizeUploadedDocumentFiles(fileId));

        console.log("[DOCUMENT-FILEID-SAVED]", { phaseId, docId, fileId });
      },

      // FileId für Dokument abrufen
      getDocumentFileId: (phaseId: string, docId: string) => {
        const docs = get().documents[phaseId] || [];
        const doc = docs.find((d) => d._id === docId);
        const files = get().getDocumentFiles(phaseId, docId);
        return getPrimaryFileId(files) || (doc as any)?.fileId || null;
      },

      saveDocumentFiles: (phaseId: string, docId: string, files: UploadedDocumentFile[]) => {
        const key = getDocumentKey(phaseId, docId);
        const normalizedFiles = mergeUploadedDocumentFiles(files);
        const currentDocs = get().documents[phaseId] || [];
        const updatedDocs = withDocumentFiles(currentDocs, docId, normalizedFiles);
        const { required: requiredCount, completed: completedCount } = recalculateProgress(updatedDocs);

        set((state) => ({
          documents: { ...state.documents, [phaseId]: updatedDocs },
          progress: {
            ...state.progress,
            [phaseId]: { required: requiredCount, completed: completedCount },
          },
          documentFiles: {
            ...state.documentFiles,
            [key]: normalizedFiles,
          },
        }));
      },

      appendDocumentFiles: (phaseId: string, docId: string, files: UploadedDocumentFile[]) => {
        const existingFiles = get().getDocumentFiles(phaseId, docId);
        const mergedFiles = mergeUploadedDocumentFiles(existingFiles, files);
        get().saveDocumentFiles(phaseId, docId, mergedFiles);
        return mergedFiles;
      },

      getDocumentFiles: (phaseId: string, docId: string) => {
        const key = getDocumentKey(phaseId, docId);
        const storedFiles = get().documentFiles[key] || [];
        if (storedFiles.length > 0) return storedFiles;

        const docs = get().documents[phaseId] || [];
        const doc = docs.find((d) => d._id === docId);
        return mergeUploadedDocumentFiles(
          normalizeUploadedDocumentFiles((doc as any)?.uploadedFiles),
          normalizeUploadedDocumentFiles((doc as any)?.fileIds),
          normalizeUploadedDocumentFiles((doc as any)?.fileId)
        );
      },
    }),
    {
      name: "360volt-document-store",
      storage: createJSONStorage(createSafeDocumentStorage),
      partialize: (state) => ({
        documents: stripDocumentsRuntimeData(state.documents),
        progress: state.progress,
        phases: state.phases,
        documentData: stripDocumentDataRuntimeData(state.documentData),
        documentFiles: Object.fromEntries(
          Object.entries(state.documentFiles).map(([key, files]) => [
            key,
            stripUploadedDocumentFilesRuntimeData(files),
          ])
        ),
      }),
    }
  )
);
